import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  HiOutlineSearch,
  HiViewGrid,
  HiViewList,
  HiPlus,
  HiOutlineEmojiSad,
  HiChevronDown,
  HiOutlineFilter,
  HiFolderAdd,
  HiChevronRight,
  HiHome,
  HiCloudUpload
} from "react-icons/hi";
import { BsGrid3X3GapFill } from "react-icons/bs";
import { motion, AnimatePresence } from "framer-motion";
import FileCard from "../Components/FileCard";
import FolderCard from "../Components/FolderCard";
import CreateFolderModal from "../Components/CreateFolderModal";
import ViewFileModal from "../Components/ViewFileModal";
import RenameModal from "../Components/RenameModal";
import ShareModal from "../Components/ShareModal";
import {
  getFoldersAPI,
  getFolderContentAPI,
  uploadFileAPI,
  renameFileAPI,
  renameFolderAPI,
  grantAccessAPI,
  deleteFolderAPI,
  deleteFileAPI
} from "../services/driveService";
import { toast } from "react-toastify";

export default function Homepage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("grid"); // 'list', 'grid', 'big-grid'
  const [activeCategory, setActiveCategory] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingFile, setViewingFile] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const fileInputRef = useRef(null);

  // Navigation State
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Home" }]);

  // Real Data State
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({ transferred: 0, total: 0, fileName: "" });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const fetchData = async () => {
    setLoadingData(true);
    try {
      if (currentFolderId === null) {
        const data = await getFoldersAPI();
        setFolders(data || []);
        setFiles([]);
      } else {
        const data = await getFolderContentAPI(currentFolderId);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      }
    } catch (error) {
      toast.error("Failed to load content");
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
    const handleRefresh = () => fetchData();
    window.addEventListener("folderCreated", handleRefresh);
    return () => window.removeEventListener("folderCreated", handleRefresh);
  }, [currentFolderId]);

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (id, index) => {
    setCurrentFolderId(id);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadStats({ transferred: 0, total: file.size, fileName: file.name });

    // const toastId = toast.loading(`Uploading ${file.name}...`);

    try {
      await uploadFileAPI(file, currentFolderId, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
        setUploadStats({
          transferred: progressEvent.loaded,
          total: progressEvent.total,
          fileName: file.name
        });
      });

      toast.success(`${file.name} uploaded successfully!`);
      // toast.update(toastId, { 
      //   render: "Uploaded successfully!", 
      //   type: "success", 
      //   isLoading: false, 
      //   autoClose: 3000 
      // });
      fetchData();
    } catch (error) {
      toast.error(error?.message || "Upload failed");
      // toast.update(toastId, { 
      //   render: error?.message || "Upload failed", 
      //   type: "error", 
      //   isLoading: false, 
      //   autoClose: 3000 
      // });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRename = async (newName) => {
    if (!renamingItem) return;
    try {
      // If item has extension, it's a file, otherwise it's a folder
      if (renamingItem.extension) {
        await renameFileAPI(renamingItem.id, newName);
      } else {
        await renameFolderAPI(renamingItem.id, newName);
      }
      toast.success("Renamed successfully!");
      fetchData();
    } catch (error) {
      toast.error(error?.message || "Rename failed");
    }
  };

  const handleShareSubmit = async (userId, permission) => {
    if (!sharingItem) return;
    try {
      // Determine resource type (File or Folder)
      // Note: backend expects lowercase usually, check curl
      const resourceType = sharingItem.extension ? "File" : "Folder";
      await grantAccessAPI(userId, sharingItem.id, resourceType, permission);
      toast.success(`Access granted to user for ${sharingItem.name}`);
      setSharingItem(null);
    } catch (error) {
      toast.error(error?.message || "Failed to grant access");
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Are you sure you want to delete folder "${folder.name}"? This will delete all its contents.`)) return;

    try {
      await deleteFolderAPI(folder.id);
      toast.success("Folder deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error?.message || "Failed to delete folder");
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Are you sure you want to delete file "${file.name}"?`)) return;

    try {
      await deleteFileAPI(file.id);
      toast.success("File deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error?.message || "Failed to delete file");
    }
  };

  const categories = [
    { id: "all", label: "All Files" },
    { id: "document", label: "Documents" },
    { id: "image", label: "Images" },
    { id: "video", label: "Videos" },
  ];

  const filteredFiles = useMemo(() => {
    return files.filter(file => {
      const matchesSearch = file.name.toLowerCase().includes(searchQuery.toLowerCase());
      const contentType = file.contentType || "";
      const extension = (file.extension || file.name.split('.').pop()).toLowerCase();

      const isDoc = contentType.includes("pdf") || contentType.includes("word") || ["pdf", "doc", "docx", "txt"].includes(extension);
      const isImg = contentType.includes("image") || ["jpg", "jpeg", "png", "gif", "svg"].includes(extension);
      const isVid = contentType.includes("video") || ["mp4", "mkv", "avi"].includes(extension);

      if (activeCategory === "document") return isDoc && matchesSearch;
      if (activeCategory === "image") return isImg && matchesSearch;
      if (activeCategory === "video") return isVid && matchesSearch;

      return matchesSearch;
    });
  }, [searchQuery, activeCategory, files]);

  return (
    <div className="mx-auto space-y-8 pb-32 px-4 md:px-0">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Search Header */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative group flex-1 max-w-2xl">
          <HiOutlineSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 text-2xl group-focus-within:text-indigo-500 transition-colors" />
          <input
            type="text"
            placeholder="Search in DriveHub..."
            className="w-full pl-16 pr-6 py-4 bg-white/70 backdrop-blur-xl border border-white shadow-2xl shadow-indigo-100/20 rounded-[2rem] outline-none focus:ring-4 focus:ring-indigo-500/10 focus:bg-white transition-all text-lg font-medium placeholder:text-slate-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 !bg-white hover:!bg-slate-50 text-indigo-600 border border-indigo-100 !px-6 !py-4 rounded-3xl shadow-lg shadow-indigo-100 transition-all active:scale-95 font-bold !w-auto !h-auto"
          >
            <HiFolderAdd className="text-xl" />
            <span className="hidden sm:inline">New Folder</span>
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 !bg-indigo-600 hover:!bg-indigo-700 disabled:opacity-50 text-white !px-8 !py-4 rounded-3xl shadow-lg shadow-indigo-200 transition-all active:scale-95 font-bold !w-auto !h-auto !border-none"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <HiCloudUpload className="text-xl" />
            )}
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>

      {/* Breadcrumbs & Universal View Toggle */}
      <div className="flex items-center justify-between py-2 border-b border-slate-200/50">
        <div className="flex items-center gap-2 text-sm overflow-x-auto no-scrollbar py-2">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id || "root"}>
              <button
                onClick={() => handleBreadcrumbClick(crumb.id, index)}
                className={`flex items-center gap-2 !px-4 !py-2 rounded-2xl transition-all font-bold whitespace-nowrap shadow-sm border !w-auto !h-auto ${index === breadcrumbs.length - 1
                  ? "!bg-white text-indigo-600 border-indigo-100 shadow-indigo-100/50"
                  : "!bg-white/50 text-slate-400 border-white hover:!bg-white hover:text-indigo-500"
                  }`}
              >
                {index === 0 && <HiHome size={16} />}
                {crumb.name}
              </button>
              {index < breadcrumbs.length - 1 && (
                <HiChevronRight className="text-slate-300" />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="flex bg-white/40 p-1 rounded-2xl border border-white/60 shadow-inner gap-1 min-w-fit ml-4">
          <button
            onClick={() => setViewType("list")}
            title="List View"
            className={`!p-2 !border-none rounded-xl transition-all duration-300 !w-auto !h-auto ${viewType === "list" ? "!bg-white text-indigo-600 shadow-md" : "text-white hover:text-slate-600 !bg-transparent"
              }`}
          >
            <HiViewList className="text-xl" />
          </button>
          <button
            onClick={() => setViewType("grid")}
            title="Grid View"
            className={`!p-2 !border-none rounded-xl transition-all duration-300 !w-auto !h-auto ${viewType === "grid" ? "!bg-white text-indigo-600 shadow-md" : "text-white hover:text-slate-600 !bg-transparent"
              }`}
          >
            <HiViewGrid className="text-xl" />
          </button>
          <button
            onClick={() => setViewType("big-grid")}
            title="Big Grid View"
            className={`p-2 rounded-xl transition-all duration-300 ${viewType === "big-grid" ? "bg-white text-indigo-600 shadow-md" : "text-white hover:text-slate-600"
              }`}
          >
            <BsGrid3X3GapFill className="text-xl" />
          </button>
        </div>
      </div>

      {/* Categories - Only show inside a folder */}
      {currentFolderId && (
        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-2.5 rounded-2xl text-sm font-bold transition-all border ${activeCategory === cat.id
                ? "bg-white text-indigo-600 border-indigo-100 shadow-lg shadow-indigo-100/50"
                : "bg-white/50 text-slate-400 border-white hover:bg-white hover:text-slate-600"
                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-12">
        {/* Folders Section */}
        {!searchQuery && (
          <section className="animate-in fade-in slide-in-from-left-4 duration-1000">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                {currentFolderId ? "Sub-folders" : "Folders"}
              </h3>
            </div>

            {loadingData ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-20 bg-white/50 animate-pulse rounded-2xl border border-white" />
                ))}
              </div>
            ) : (
              <div className={`
                ${viewType === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" : ""}
                ${viewType === "big-grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" : ""}
                ${viewType === "list" ? "flex flex-col gap-2" : ""}
              `}>
                <AnimatePresence mode="popLayout">
                  {folders.map((folder) => (
                    <FolderCard
                      key={folder.id}
                      folder={folder}
                      view={viewType === "list" ? "list" : "grid"}
                      isBig={viewType === "big-grid"}
                      onClick={() => handleFolderClick(folder)}
                      onRename={setRenamingItem}
                      onShare={setSharingItem}
                      onDelete={handleDeleteFolder}
                    />
                  ))}
                </AnimatePresence>
                {folders.length === 0 && !loadingData && (
                  <div className="col-span-full py-10 text-center bg-white/30 rounded-3xl border-2 border-dashed border-white/50">
                    <p className="text-slate-400 font-medium italic">No folders here</p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Files Section */}
        {currentFolderId && (
          <section className="animate-in fade-in slide-in-from-right-4 duration-1000 delay-150">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 tracking-tight">
                Files
              </h3>
            </div>

            {loadingData ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-40 bg-white/50 animate-pulse rounded-2xl border border-white" />
                ))}
              </div>
            ) : (
              <div className={`
                ${viewType === "grid" ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6" : ""}
                ${viewType === "big-grid" ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8" : ""}
                ${viewType === "list" ? "flex flex-col gap-2" : ""}
              `}>
                <AnimatePresence mode="popLayout">
                  {filteredFiles.map((file) => {
                    const extension = file.extension || file.name.split('.').pop();
                    return (
                      <FileCard
                        key={file.id}
                        file={{ ...file, extension }}
                        view={viewType === "list" ? "list" : "grid"}
                        isBig={viewType === "big-grid"}
                        onView={() => setViewingFile(file)}
                        onRename={setRenamingItem}
                        onShare={setSharingItem}
                        onDelete={handleDeleteFile}
                      />
                    );
                  })}
                </AnimatePresence>

                {filteredFiles.length === 0 && !loadingData && (
                  <div className="col-span-full py-20 text-center rounded-[3rem] bg-white/20 border-2 border-dashed border-white/40">
                    <div className="flex flex-col items-center gap-4 text-slate-400">
                      <HiOutlineEmojiSad size={48} className="opacity-20" />
                      <p className="font-bold">No files found here</p>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-indigo-600 text-sm hover:underline font-bold"
                      >
                        Upload your first file
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      <CreateFolderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => fetchData()}
        parentId={currentFolderId}
      />

      <ViewFileModal
        file={viewingFile}
        isOpen={!!viewingFile}
        onClose={() => setViewingFile(null)}
      />

      <RenameModal
        isOpen={!!renamingItem}
        onClose={() => setRenamingItem(null)}
        currentName={renamingItem?.name}
        onRename={handleRename}
      />

      <ShareModal
        isOpen={!!sharingItem}
        onClose={() => setSharingItem(null)}
        resource={sharingItem}
        onShare={handleShareSubmit}
      />

      {/* Centered Upload Progress Indicator */}
      <AnimatePresence>
        {uploading && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] border border-white shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] p-8 overflow-hidden"
            >
              {/* Background Glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 bg-indigo-500/10 blur-[64px] pointer-events-none" />

              <div className="relative z-10 flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-indigo-50 rounded-[28px] flex items-center justify-center text-indigo-600 border border-indigo-100 mb-6 relative group">
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <HiCloudUpload size={40} />
                  </motion.div>

                  {/* Circular Progress Track */}
                  <svg className="absolute inset-0 w-full h-full -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="transparent"
                      className="text-indigo-100"
                    />
                    <motion.circle
                      cx="40"
                      cy="40"
                      r="36"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="transparent"
                      strokeDasharray="226.2"
                      initial={{ strokeDashoffset: 226.2 }}
                      animate={{ strokeDashoffset: 226.2 - (226.2 * uploadProgress) / 100 }}
                      className="text-indigo-600"
                    />
                  </svg>
                </div>

                <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-2">
                  Uploading File
                </h3>
                <p className="text-slate-500 font-bold text-sm mb-8 px-4 line-clamp-1">
                  {uploadStats.fileName}
                </p>

                <div className="w-full space-y-4">
                  <div className="flex justify-between items-end mb-1">
                    <span className="text-indigo-600 font-black text-3xl">
                      {uploadProgress}%
                    </span>
                    <div className="flex flex-col items-end text-[10px] font-black uppercase tracking-wider text-slate-400">
                      <span>{formatFileSize(uploadStats.transferred)}</span>
                      <span>of {formatFileSize(uploadStats.total)}</span>
                    </div>
                  </div>

                  <div className="h-4 w-full bg-slate-100 rounded-2xl overflow-hidden p-1">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 rounded-xl shadow-[0_0_12px_rgba(99,102,241,0.4)]"
                    />
                  </div>

                  <div className="flex justify-center">
                    <span className="bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest border border-emerald-100">
                      {formatFileSize(Math.max(0, uploadStats.total - uploadStats.transferred))} remaining
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
